import { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Skeleton, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';

interface FinanceRow {
  key: string;
  metric: string;
  today: { value: number; change: number };
  thisWeek: { value: number; change: number };
  thisMonth: { value: number; change: number };
  lastMonth: { value: number; change: number };
  thisYear: { value: number; change: number };
  lastYearYTD: { value: number; change: number };
  isCurrency: boolean;
}

interface FinanceTableProps {
  loading: boolean;
  error?: string | null;
  data: any[];
}

export const FinanceTable = ({ loading, error, data }: FinanceTableProps) => {
  const { userInfo } = useAuth();
  const currencySymbol = userInfo?.company_base_currency_symbol || '$';
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Map the data based on label and appropriate fields
  const mapDataToRows = (data: any[]): FinanceRow[] => {
    return data.map((item, index) => {
      let valueField: string;
      let changeField: string;
      let isCurrency = false;

      // Determine which fields to use based on the label
      switch (item.label) {
        case 'SO Raised Nos':
          valueField = 'so_count';
          changeField = 'percent_change_so_count';
          isCurrency = false;
          break;
        case 'SO Raised in Values':
          valueField = 'so_value';
          changeField = 'percent_change_so_value';
          isCurrency = true;
          break;
        case 'No of SO Invoices':
          valueField = 'so_invoice_count';
          changeField = 'percent_change_so_invoice_count';
          isCurrency = false;
          break;
        case 'SO Invoice Values':
          valueField = 'sales_total'; // As mentioned, SO Invoice Values means sales_total
          changeField = 'percent_change_sales_total';
          isCurrency = true;
          break;
        default:
          valueField = 'so_count';
          changeField = 'percent_change_so_count';
          isCurrency = false;
      }

      return {
        key: `finance-${index}`,
        metric: item.label,
        today: {
          value: (item as any)[`today_${valueField}`] || 0,
          change: (item as any)[`today_${changeField}`] || 0
        },
        thisWeek: {
          value: (item as any)[`this_week_${valueField}`] || 0,
          change: (item as any)[`this_week_${changeField}`] || 0
        },
        thisMonth: {
          value: (item as any)[`this_month_${valueField}`] || 0,
          change: (item as any)[`this_month_${changeField}`] || 0
        },
        lastMonth: {
          value: (item as any)[`last_month_${valueField}`] || 0,
          change: (item as any)[`last_month_${changeField}`] || 0
        },
        thisYear: {
          value: (item as any)[`this_year_to_date_${valueField}`] || 0,
          change: (item as any)[`this_year_to_date_${changeField}`] || 0
        },
        lastYearYTD: {
          value: (item as any)[`last_year_to_date_${valueField}`] || 0,
          change: (item as any)[`last_year_to_date_${changeField}`] || 0
        },
        isCurrency
      };
    });
  };

  // Create skeleton data when loading
  const createSkeletonData = (): FinanceRow[] => {
    const defaultRows = [
      { key: 'skeleton-1', label: 'SO Raised Nos', isCurrency: false },
      { key: 'skeleton-2', label: 'SO Raised in Values', isCurrency: true },
      { key: 'skeleton-3', label: 'No of SO Invoices', isCurrency: false },
      { key: 'skeleton-4', label: 'SO Invoice Values', isCurrency: true }
    ];

    return defaultRows.map(row => ({
      key: row.key,
      metric: row.label,
      today: { value: 0, change: 0 },
      thisWeek: { value: 0, change: 0 },
      thisMonth: { value: 0, change: 0 },
      lastMonth: { value: 0, change: 0 },
      thisYear: { value: 0, change: 0 },
      lastYearYTD: { value: 0, change: 0 },
      isCurrency: row.isCurrency
    }));
  };

  const tableData: FinanceRow[] = loading ? createSkeletonData() : mapDataToRows(data);

  const formatValue = useCallback((valueData: { value: number; change: number }, isCurrency: boolean) => {
    if (loading) return <Skeleton.Input active size="small" style={{ width: 80 }} />;

    const { value, change } = valueData;
    const isPositive = change >= 0;
    const formattedValue = isCurrency
      ? `${currencySymbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const formattedChange = `${isPositive ? '+' : ''}${change.toFixed(2)}%`;
    const changeColor = isPositive ? 'text-green-600' : 'text-red-600';
    const TrendIcon = isPositive ? TrendingUp : TrendingDown;

    return (
      <div className="text-center">
        <div className="font-medium text-gray-900 mb-1">
          {formattedValue}
        </div>
        <div className={`flex items-center justify-center gap-1 text-xs font-medium ${changeColor}`}>
          <TrendIcon className="h-3 w-3" />
          {formattedChange}
        </div>
      </div>
    );
  }, [currencySymbol, loading]);

  const columns: ColumnsType<FinanceRow> = useMemo(() => [
    {
      title: loading ? <Skeleton.Input active size="small" style={{ width: 80 }} /> : 'Metric',
      dataIndex: 'metric',
      key: 'metric',
      width: isMobile ? 160 : 200,
      fixed: isMobile ? undefined : 'left',
      className: isMobile ? 'bg-white' : undefined,
      render: (value: string) => loading ? <Skeleton.Input active size="small" /> : (
        <div className="flex items-center gap-2">
          <strong className="text-gray-800">{value}</strong>
        </div>
      )
    },
    {
      title: loading ? <Skeleton.Input active size="small" style={{ width: 60 }} /> : 'Today',
      dataIndex: 'today',
      key: 'today',
      width: 140,
      align: 'center',
      render: (value: { value: number; change: number }, record: FinanceRow) =>
        formatValue(value, record.isCurrency)
    },
    {
      title: loading ? <Skeleton.Input active size="small" style={{ width: 80 }} /> : 'This Week',
      dataIndex: 'thisWeek',
      key: 'thisWeek',
      width: 140,
      align: 'center',
      render: (value: { value: number; change: number }, record: FinanceRow) =>
        formatValue(value, record.isCurrency)
    },
    {
      title: loading ? <Skeleton.Input active size="small" style={{ width: 90 }} /> : 'This Month',
      dataIndex: 'thisMonth',
      key: 'thisMonth',
      width: 150,
      align: 'center',
      render: (value: { value: number; change: number }, record: FinanceRow) =>
        formatValue(value, record.isCurrency)
    },
    {
      title: loading ? <Skeleton.Input active size="small" style={{ width: 90 }} /> : 'Last Month',
      dataIndex: 'lastMonth',
      key: 'lastMonth',
      width: 150,
      align: 'center',
      render: (value: { value: number; change: number }, record: FinanceRow) =>
        formatValue(value, record.isCurrency)
    },
    {
      title: loading ? <Skeleton.Input active size="small" style={{ width: 80 }} /> : 'This Year YTD',
      dataIndex: 'thisYear',
      key: 'thisYear',
      width: 150,
      align: 'center',
      render: (value: { value: number; change: number }, record: FinanceRow) =>
        formatValue(value, record.isCurrency)
    },
    {
      title: loading ? <Skeleton.Input active size="small" style={{ width: 100 }} /> : 'Last Year YTD',
      dataIndex: 'lastYearYTD',
      key: 'lastYearYTD',
      width: 150,
      align: 'center',
      render: (value: { value: number; change: number }, record: FinanceRow) =>
        formatValue(value, record.isCurrency)
    }
  ], [formatValue, isMobile, loading]);

  return (
    <div className="text-left">
      {error && <Alert message={error} type="error" showIcon className="mb-4" />}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Financial Performance Overview</h3>
          </div>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={false}
              size="middle"
              loading={false}
              scroll={{ x: isMobile ? 'max-content' : 1200 }}
              className="finance-overview-table"
              rowClassName={(record, index) =>
                `transition-colors duration-200 ${index % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'} hover:bg-blue-50/30`
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceTable;